import React, { useEffect, useState } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import ModalBankAccount from "./component/ModalBankAccount";
import { useBankAccount } from "../useHook";
import Swal from "sweetalert2";
import Loader from "../../../Utils/loader";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

function BankAccount() {
  const {
    getAllBankAccounts,
    addBankAccount,
    deleteBankAccount,
    editBankAccountDetails,
  } = useBankAccount();
  const [showModal, setShowModal] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [editingAccount, setEditingAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.BankAccountDetails.CREATE);
  const canUpdate = hasPermission(SystemPermissions.BankAccountDetails.UPDATE);
  const canDelete = hasPermission(SystemPermissions.BankAccountDetails.DELETE);

  useEffect(() => {
    fetchBankAccountsData();
  }, []);

  const fetchBankAccountsData = async () => {
    try {
      setIsLoading(true);
      await getAllBankAccounts(setBankAccounts);
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingAccount(null);
    setShowModal(true);
  };

  const handleSave = async (newAccount) => {
    try {
      if (editingAccount) {
        const response = await editBankAccountDetails(
          editingAccount.id,
          newAccount,
        );
        if (!response?.success) {
          throw new Error(response?.message || "Failed to update bank account.");
        }

        const updatedAccount = response.data;
        setBankAccounts((prevAccounts) =>
          prevAccounts.map((account) =>
            account.id === editingAccount.id ? updatedAccount : account,
          ),
        );

        Swal.fire({
          title: "Success!",
          text: response.message || "Bank account updated successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        const response = await addBankAccount(newAccount);
        if (!response?.success) {
          throw new Error(response?.message || "Failed to add bank account.");
        }

        const createdAccount = response.data;
        if (createdAccount) {
          setBankAccounts((prevAccounts) => [...prevAccounts, createdAccount]);
        }

        Swal.fire({
          title: "Success!",
          text: response.message || "Bank account added successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      }
      setShowModal(false);
      await getAllBankAccounts(setBankAccounts);
    } catch (error) {
      console.error("Error saving bank account:", error);
      Swal.fire({
        title: "Error!",
        text:
          error?.message ||
          "There was an issue saving the bank account.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteBankAccount(id);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to delete bank account.");
      }

      setBankAccounts((prevAccounts) =>
        prevAccounts.filter((account) => account.id !== id),
      );
      Swal.fire({
        title: "Deleted!",
        text: response.message || "Bank account deleted successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });
      await getAllBankAccounts(setBankAccounts);
    } catch (error) {
      console.error("Error deleting bank account:", error);
      Swal.fire({
        title: "Error!",
        text:
          error?.message ||
          "There was an issue deleting the bank account.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  if (isLoading) {
    return <Loader message="Loading bank accounts..." />;
  }

  const showActions = canUpdate || canDelete;

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Bank Account Details</h2>

        {canCreate && (
          <button onClick={handleAddClick} className="btn btn-primary h-[44px]">
            <FiPlus className="text-white" />
            Add Bank Account
          </button>
        )}
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow my-5">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr No.</th>
                <th className="table-th">Bank Name</th>
                <th className="table-th">Account Holder Name</th>
                <th className="table-th">Account Number</th>
                <th className="table-th">IBAN</th>
                <th className="table-th">Branch Code</th>
                {showActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {bankAccounts.length === 0 ? (
                <tr>
                  <td colSpan={showActions ? 7 : 6} className="text-center py-6 text-gray-500">
                    No data found
                  </td>
                </tr>
              ) : (
                bankAccounts.map((account, index) => (
                  <tr
                    key={account.accountNumber}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{index + 1}</td>
                    <td className="table-td max-w-[200px]">
                      {account.bankName}
                    </td>
                    <td className="table-td ">{account.accountHolderName}</td>
                    <td className="table-td max-w-[200px]">
                      {account.accountNumber}
                    </td>
                    <td className="table-td max-w-[220px]">{account.iban}</td>
                    <td className="table-td">{account.branchCode}</td>
                    {showActions && (
                      <td className="table-td flex space-x-1">
                        {canUpdate && (
                          <button
                            className="btn btn-edit text-white"
                            onClick={() => handleEdit(account)}
                          >
                            <FiEdit2 className="text-white" /> Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn btn-delete text-white"
                            onClick={() => handleDelete(account.id)}
                          >
                            <MdDelete className="text-white" /> Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ModalBankAccount
        show={showModal}
        close={handleCloseModal}
        settingData={editingAccount}
        handleSave={handleSave}
      />
    </div>
  );
}

export default BankAccount;
